import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ISave } from '@app-settings/shared/models/i-save.model';
import { SettingsService } from '@app-settings/shared/settings.service';
import { BaseComponent } from '@core/components/base-component';
import { License, LicensedUserDTO, LicenseModule } from '@interfaces/LicenseController';
import { UserDto } from '@interfaces/UserDto';
import { LicenseService } from '@shared/services/license.service';
import { lastValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';

@Component({
  selector: 'pop-license-user-table',
  templateUrl: './license-user-table.component.html',
  styleUrls: ['../../settings.component.less', './license-user-table.component.less']
})
export class LicenseUserTableComponent extends BaseComponent implements OnInit, ISave {
  public resources: LicensedUserDTO[];
  public modules: LicenseModule[] = [];

  @Output()
  public showError = new EventEmitter<string>();

  private _verifiedLicense: License;
  @Input()
  public set verifiedLicense(value) {
    this._verifiedLicense = value;
    this.onLicenseUpdated();
  }
  public get verifiedLicense() {
    return this._verifiedLicense;
  }

  private addLicensesChange: LicensedUserDTO[] = [];
  private removeLicensesChange: LicensedUserDTO[] = [];
  public userCountPerModule = new Map<string, number>();

  public constructor(private licenseService: LicenseService, private popSettingsService: SettingsService) {
    super();
  }

  public ngOnInit(): void {
    this.onLicenseUpdated();
  }

  private setLicensedUsers(): void {
    this.licenseService
      .getLicensedUsers()
      .pipe(first())
      .subscribe(licensedUsers => {
        this.resources = licensedUsers;
        this.setUserCountPerModules();
      });
  }

  private setLimitedModulesFromLicense(): void {
    this.modules = [];
    this.verifiedLicense?.modules.forEach((module: LicenseModule) => {
      if (module.number != null) {
        this.modules.push(module);
      }
    });
  }

  private setUserCountPerModules(): void {
    this.userCountPerModule = new Map<string, number>();
    this.verifiedLicense?.modules.forEach((module: LicenseModule) => {
      if (module.number != null) {
        this.userCountPerModule.set(
          module.moduleId,
          this.resources.filter(r => r.modules.includes(module.moduleId)).length
        );
      }
    });
  }

  private onLicenseUpdated() {
    this.setLimitedModulesFromLicense();
    this.setLicensedUsers();
  }

  public addUser(user: UserDto | undefined) {
    if (!user) return;

    if (!this.resources.find(r => r.email === user.email)) {
      const resource: LicensedUserDTO = {
        email: user.email,
        modules: []
      };
      this.resources.unshift(resource);
      this.resources = this.resources.slice();
    }
  }

  public async onCheckboxChange(userEmail: string, moduleId: string, checkbox, event: any) {
    if (event.checked) {
      const isTooMany = await this.licenseService.isTooManyUsersForModule(moduleId, true);

      const isTooManyLocally =
        this.userCountPerModule.get(moduleId) >= this.modules.find(m => m.moduleId === moduleId).number;
      if (!isTooMany && !isTooManyLocally) {
        this.resources.find(r => r.email === userEmail).modules.push(moduleId);
        this.addLicensedUserToChangeList(this.addLicensesChange, this.removeLicensesChange, userEmail, moduleId);
        this.onChange();
        this.userCountPerModule.set(moduleId, this.userCountPerModule.get(moduleId) + 1);
      } else {
        this.showError.emit('ERROR.LICENSE_TOO_MANY_USERS_SHORT');
        checkbox.checked = false;
      }

      return;
    }

    const index = this.resources.find(r => r.email === userEmail).modules.findIndex(m => m === moduleId);
    this.resources.find(r => r.email === userEmail).modules.splice(index, 1);
    this.userCountPerModule.set(moduleId, this.userCountPerModule.get(moduleId) - 1);

    this.addLicensedUserToChangeList(this.removeLicensesChange, this.addLicensesChange, userEmail, moduleId);
    this.onChange();
  }

  private onChange() {
    this.popSettingsService.needsSave();
    this.popSettingsService.onSave(this);
  }

  /**
   * Will be called by app-settings when the save button is active and pressed.
   */
  public async onSave() {
    if (this.removeLicensesChange.length || this.addLicensesChange.length) {
      const [licensedUsers] = await Promise.all([
        lastValueFrom(this.licenseService.addModulesForLicensedUsers(this.addLicensesChange)),
        lastValueFrom(this.licenseService.removeModulesFromLicensedUsers(this.removeLicensesChange))
      ]);

      licensedUsers.forEach(
        user => (this.resources.find(resource => resource.email === user.email).modules = user.modules)
      );
      this.removeLicensesChange = [];
      this.addLicensesChange = [];
      this.resources = this.resources.filter(r => r.modules.length !== 0);
    }

    location.reload(); // refresh page (for now)
  }

  /**
   * two changelist exists in component:
   * removeLicensesChange, addLicensesChange
   *
   * When we add a (user,module) to removeLicensesChange, we don't want
   * the same (user,module) to be in addLicensesChange. In this case,
   * listToAdd would be removeLicensesChange and listToRemove would be
   * addLicensesChange. These are opposite lists, when we add to one, we
   * need to remove from the other one *if the (user,module) exists in it*
   *
   *
   * @param listToAdd the list in which we want to add the user
   * @param listToRemove the opposite list in which the user might already be
   * @param userEmail
   * @param moduleId
   */
  private addLicensedUserToChangeList(
    listToAdd: LicensedUserDTO[],
    listToRemove: LicensedUserDTO[],
    userEmail: string,
    moduleId: string
  ): void {
    // Case 1: The manager reverts a current decision. i.e. he selected a user, then unselect.
    //if we find the (user, module) in the opposite changeList, we delete it from that list and return
    const userIndexInAddChanges = listToRemove.findIndex(r => r.email === userEmail);
    if (userIndexInAddChanges !== -1) {
      const moduleIndex = listToRemove[userIndexInAddChanges].modules.findIndex(m => m === moduleId);
      if (moduleIndex !== -1) {
        if (listToRemove[userIndexInAddChanges].modules.length === 1) {
          listToRemove.splice(userIndexInAddChanges, 1);
        } else {
          listToRemove[userIndexInAddChanges].modules.splice(moduleIndex, 1);
        }
        return;
      }
    }

    // Case 2: We add the user to the specified changeList
    const userIndexInChanges = listToAdd.findIndex(licensedUser => licensedUser.email === userEmail);
    if (userIndexInChanges !== -1) {
      //user exists, add the module
      listToAdd[userIndexInChanges].modules.push(moduleId);
    } else {
      //user inexistant, add the user and the module
      listToAdd.push({ email: userEmail, modules: [moduleId] });
    }
  }
}
